import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/web-ui/components/dialog";
import { Button } from "@workspace/web-ui/components/button";
import { Input } from "@workspace/web-ui/components/input";
import { Textarea } from "@workspace/web-ui/components/textarea";
import { Label } from "@workspace/web-ui/components/label";
import { DollarSign, Loader2 } from "lucide-react";
import { useCreateTaskMutation } from "~/features/dashboard/api/tasks/mutation";
import { PatientSessions } from "../api/types";
import { toast } from "sonner";
import { useActiveSessionStore } from "../store/active-session-store";
import { useParams } from "@tanstack/react-router";
import { getApiProgram } from "~/lib/utils";
import { FormattedEncounter } from "../../encounters/types";

type SessionType = PatientSessions['sessions'][0];

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSession?: SessionType;
  encounter?: FormattedEncounter;
}

export const AppointmentModal = ({ isOpen, onClose, encounter }: AppointmentModalProps) => {
  const [cost, setCost] = useState("50");
  const [instructions, setInstructions] = useState("");
  const { selectedSession } = useActiveSessionStore()
  const { program } = useParams({ strict: false });

  const createTaskMutation = useCreateTaskMutation();

  const handleAssign = async () => {
    if (!selectedSession?.session_id && !encounter?.session?.session_id) {
      toast.error("No session selected");
      return;
    }

    try {
      const payload = {
        encounter_id: encounter?.encounterId || selectedSession?.encounter?.encounter_id || "",
        type: "appointment_followup",
        cost: Number(cost.replace('$', '')),
        instructions: instructions,
        program: getApiProgram(program ?? ""),
        session_id: encounter?.session?.session_id || selectedSession?.session_id || "",
      };

      await createTaskMutation.mutateAsync(payload);

      toast.success("Appointment assigned successfully");
      handleClose();
    } catch (error) {
      console.error("Error assigning appointment:", error);
      toast.error("Failed to assign appointment");
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form
    setCost("50");
    setInstructions("");
  };

  const isSubmitting = createTaskMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="relative">
          <DialogTitle className="text-xl font-semibold">Schedule Appointment</DialogTitle>
          {/* <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-6 w-6"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button> */}
        </DialogHeader>

        <div className="space-y-6">
          {/* Cost Field */}
          <div className="space-y-2">
            <Label htmlFor="cost" className="text-sm font-medium">
              Cost
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                id="cost"
                type="text"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="pl-10"
                placeholder="0"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Instructions Field */}
          <div className="space-y-2">
            <Label htmlFor="instructions" className="text-sm font-medium">
              Instructions
            </Label>
            <div className="relative">
              <Textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Instructions"
                className="min-h-[100px] resize-none"
                maxLength={200}
                disabled={isSubmitting}
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                {instructions.length}/200
              </div>
            </div>
          </div>

          {/* Assign Button */}
          <Button
            onClick={handleAssign}
            className="w-full bg-[#4272DD] rounded-[8px] text-white py-3"
          // disabled={isSubmitting || !selectedSession?.session_id}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              "Assign to patient"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 